import create from "zustand";
import { log } from "../log";
import { proxyFetch, redirectToLogout } from "./proxy-fetch";

type UrlFn<T = any> = (args?: T) => string;
type BodyFn<T = any> = (args?: T) => object;

export type AysncFn<T = any, U = any> = (args?: T) => Promise<U>;
export interface FetchRequestProps<T = any> {
  method: string;
  form?: boolean;
  disableBodyEdit?: boolean;
  microservice?: boolean;
  url?: UrlFn<T>;
  body?: BodyFn<T>;
  notFoundResult?: (response?: any) => any;
  cacheResult?: (args?: T) => any;
  onResult?: (args?: T, result?: any) => any;
}

type RequestActivity = {
  active?: boolean;
};
export const requestActivity = create<RequestActivity>(() => ({
  active: false,
}));

export function onSessionExpired() {
  redirectToLogout();
}

export function fetchRequest<T = any, U = any>(
  props: FetchRequestProps<T>
): AysncFn<T, U> {
  let { microservice, url, notFoundResult, cacheResult, onResult } = props;

  return async (args?: T) => {
    requestActivity.setState({ active: true });

    if (cacheResult) {
      const result = cacheResult(args);
      if (result) {
        requestActivity.setState({ active: false });
        return Promise.resolve(result);
      }
    }
    const body: any = props.body ? props.body(args) : undefined;
    const service = body?.service;
    if (microservice || service === "worksheet" || service === "coding-queue") {
      url = () => `/${service}/${body.action}`;
      body.service = undefined;
    }
    // console.info("fetchRequest", url, body);
    try {
      const result = await proxyFetch({
        ...props,
        body,
        url: url ? url(args) : null,
        redirectOnUnauthorized: true,
      });

      onResult && onResult(args, result);
      requestActivity.setState({ active: false });
      return result;
    } catch (error) {
      log.error(error);
      requestActivity.setState({ active: false });
      notFoundResult && notFoundResult(error);
      return Promise.reject(error);
    }
  };
}

const controller = new AbortController();
const signal = controller.signal;

export function authHeader(token: string, init?: RequestInit): RequestInit {
  if (!token || !token.length) {
    console.warn("missing jwt token");
  }
  return {
    ...init,
    ...{
      signal,
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
      },
    },
  };
}

export type Request<T = any, U = any> = {
  data: U;
  loading: boolean;
  error: any;
  fetch(args?: T): void | Promise<any>;
};

export interface RequestProps<T = any, U = any> {
  once?: boolean;
  fn: AysncFn<T, U>;
  onSuccess?: (value: U) => void;
  onError?: (error?: any) => void;
  postProcess?: (reponse?: any) => U;
  notFoundResult?: (reponse?: any) => U;
}
